import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes, FaExpand } from 'react-icons/fa';
import { getStaticContent } from '../constants/staticContent';

import fullViewMain from '../assets/image_gallery/full_view_main.png';
import withPerson from '../assets/image_gallery/with_person.png';
import machineView1 from '../assets/image_gallery/cropshield_machin_view (1).png';
import machineView2 from '../assets/image_gallery/cropshield_machin_view (2).png';
import machineView3 from '../assets/image_gallery/cropshield_machin_view (3).png';
import machineView4 from '../assets/image_gallery/cropshield_machin_view (4).png';
import machineView5 from '../assets/image_gallery/cropshield_machin_view (5).png';
import machineView6 from '../assets/image_gallery/cropshield_machin_view (6).png';
import machineView7 from '../assets/image_gallery/cropshield_machin_view (7).png';
import machineView8 from '../assets/image_gallery/cropshield_machin_view (8).png';
import results1 from '../assets/image_gallery/results (1).png';
import results2 from '../assets/image_gallery/results (2).png';
import results3 from '../assets/image_gallery/results (3).png';
import results4 from '../assets/image_gallery/results (4).png';
import closeView1 from '../assets/image_gallery/closeview (1).png';
import closeView2 from '../assets/image_gallery/closeview (2).png';

// Image list — captions are resolved by language via `captionKey` at render time.
const GALLERY_IMAGES = [
  { src: fullViewMain, category: 'field', captionKey: 'inAction', featured: true },
  { src: machineView1, category: 'device', captionKey: 'deviceFront' },
  { src: machineView2, category: 'device', captionKey: 'deviceSide' },
  { src: withPerson, category: 'field', captionKey: 'farmerFriendly', featured: true },
  { src: machineView3, category: 'device', captionKey: 'solarMount' },
  { src: machineView4, category: 'device', captionKey: 'weatherBuild' },
  { src: results1, category: 'results', captionKey: 'results1' },
  { src: results2, category: 'results', captionKey: 'results2' },
  { src: machineView5, category: 'device', captionKey: 'ruggedField' },
  { src: closeView1, category: 'closeup', captionKey: 'closeTrap' },
  { src: machineView6, category: 'device', captionKey: 'readyInstall' },
  { src: results3, category: 'results', captionKey: 'results3' },
  { src: closeView2, category: 'closeup', captionKey: 'closeUv' },
  { src: machineView7, category: 'device', captionKey: 'fullySolar' },
  { src: machineView8, category: 'device', captionKey: 'builtToLast' },
  { src: results4, category: 'results', captionKey: 'results4' },
];

const Gallery = ({ language = 'en' }) => {
  const { GALLERY_CONTENT } = getStaticContent(language);
  const CATEGORIES = GALLERY_CONTENT.categories;
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  const filteredImages = (
    activeCategory === 'all'
      ? GALLERY_IMAGES
      : GALLERY_IMAGES.filter((img) => img.category === activeCategory)
  ).map((img) => ({ ...img, ...GALLERY_CONTENT.captions[img.captionKey] }));

  // Reveal-on-scroll for the whole section (items stagger via delay)
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);

  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + filteredImages.length) % filteredImages.length));
  }, [filteredImages.length]);

  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % filteredImages.length));
  }, [filteredImages.length]);

  // Keyboard navigation + scroll lock while the lightbox is open
  useEffect(() => {
    if (lightboxIndex === null) return;
    const handleKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') showPrev();
      if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const handleFilterChange = (categoryId) => {
    setActiveCategory(categoryId);
    setLightboxIndex(null);
  };

  const lightboxImage = lightboxIndex !== null ? filteredImages[lightboxIndex] : null;

  return (
    <section
      id="gallery"
      ref={sectionRef}
      className="min-h-screen flex items-center bg-gradient-to-br from-teal-50 via-white to-emerald-50 py-16 md:py-20 px-4 md:px-8 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto w-full">
        {/* Header */}
        <div
          className={`text-center mb-10 md:mb-12 transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <div className="inline-block mb-4">
            <span className="text-xs font-bold tracking-widest uppercase text-emerald-700 bg-emerald-100 px-4 py-1.5 rounded-full border border-emerald-300">
              {GALLERY_CONTENT.sectionLabel}
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            {GALLERY_CONTENT.titleLead} <span className="text-emerald-600">{GALLERY_CONTENT.titleHighlight}</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 leading-relaxed max-w-3xl mx-auto">
            {GALLERY_CONTENT.description}
          </p>
        </div>

        {/* Category Filters */}
        <div
          className={`flex flex-wrap justify-center gap-2 md:gap-3 mb-10 md:mb-12 transition-all duration-700 delay-150 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {CATEGORIES.map((category) => (
            <button
              key={category.id}
              onClick={() => handleFilterChange(category.id)}
              className={`px-4 md:px-6 py-2 md:py-2.5 rounded-full text-sm md:text-base font-semibold transition-all duration-300 border-2 ${
                activeCategory === category.id
                  ? 'bg-emerald-600 text-white border-emerald-600 shadow-lg shadow-emerald-600/30 scale-105'
                  : 'bg-white text-gray-600 border-gray-200 hover:border-emerald-400 hover:text-emerald-700 hover:shadow-md'
              }`}
            >
              {category.label}
            </button>
          ))}
        </div>

        {/* Image Grid — keyed by category so items re-animate on filter change */}
        <div
          key={activeCategory}
          className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[150px] sm:auto-rows-[180px] md:auto-rows-[200px] gap-3 md:gap-4"
        >
          {filteredImages.map((image, index) => (
            <button
              key={image.src}
              onClick={() => setLightboxIndex(index)}
              className={`gallery-item group relative rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-emerald-400/60 ${
                image.featured && activeCategory === 'all' ? 'col-span-2 row-span-2' : ''
              } ${isVisible ? 'gallery-item-visible' : ''}`}
              style={{ animationDelay: `${Math.min(index * 60, 600)}ms` }}
              aria-label={`View ${image.title} — ${image.caption}`}
            >
              <img
                src={image.src}
                alt={image.caption}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-110"
              />
              {/* Hover overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-gray-900/80 via-gray-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              {/* Caption */}
              <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 text-left translate-y-4 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300 ease-out">
                <p className="text-white font-bold text-sm md:text-base leading-tight">{image.title}</p>
                <p className="text-emerald-200 text-xs md:text-sm leading-snug mt-0.5 hidden sm:block">{image.caption}</p>
              </div>
              {/* Expand icon */}
              <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 text-emerald-700 flex items-center justify-center opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100 transition-all duration-300 shadow-md">
                <FaExpand className="w-3.5 h-3.5" />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxImage && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-gray-950/90 backdrop-blur-sm lightbox-fade"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label="Image viewer"
        >
          {/* Close */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white flex items-center justify-center transition-colors duration-200 z-10"
            aria-label="Close image viewer"
          >
            <FaTimes className="w-5 h-5" />
          </button>

          {/* Counter */}
          <div className="absolute top-5 left-1/2 -translate-x-1/2 md:top-7 text-white/80 text-sm font-semibold tracking-wide">
            {lightboxIndex + 1} / {filteredImages.length}
          </div>

          {/* Prev */}
          <button
            onClick={(e) => { e.stopPropagation(); showPrev(); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-emerald-600 text-white flex items-center justify-center transition-all duration-200 hover:scale-110 z-10"
            aria-label="Previous image"
          >
            <FaChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
          </button>

          {/* Image + caption */}
          <div
            className="max-w-[90vw] max-h-[85vh] flex flex-col items-center lightbox-zoom"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={lightboxImage.src}
              src={lightboxImage.src}
              alt={lightboxImage.caption}
              className="max-w-full max-h-[72vh] object-contain rounded-xl shadow-2xl"
            />
            <div className="mt-4 text-center px-4">
              <p className="text-white font-bold text-base md:text-lg">{lightboxImage.title}</p>
              <p className="text-emerald-300 text-sm md:text-base mt-1">{lightboxImage.caption}</p>
            </div>
          </div>

          {/* Next */}
          <button
            onClick={(e) => { e.stopPropagation(); showNext(); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-emerald-600 text-white flex items-center justify-center transition-all duration-200 hover:scale-110 z-10"
            aria-label="Next image"
          >
            <FaChevronRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>
      )}

      <style>{`
        .gallery-item {
          opacity: 0;
          transition: box-shadow 0.3s ease;
        }
        @keyframes galleryItemIn {
          from { opacity: 0; transform: translateY(24px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .gallery-item-visible {
          animation: galleryItemIn 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        @keyframes lightboxFade {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .lightbox-fade {
          animation: lightboxFade 0.25s ease-out forwards;
        }
        @keyframes lightboxZoom {
          from { opacity: 0; transform: scale(0.92); }
          to { opacity: 1; transform: scale(1); }
        }
        .lightbox-zoom {
          animation: lightboxZoom 0.3s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
        @media (prefers-reduced-motion: reduce) {
          .gallery-item, .lightbox-fade, .lightbox-zoom {
            transition: none;
            animation: none;
            opacity: 1;
            transform: none;
          }
        }
      `}</style>
    </section>
  );
};

export default Gallery;
